import React, { useState, useMemo } from 'react';
import { useRoster } from '../hooks/useRoster';

const ATTR_LABELS = {
    flagPulling: 'Flag Pulling',
    defensiveRushing: 'Defensive Rushing',
    defensiveCoverage: 'Coverage',
    catching: 'Catching',
    agility: 'Agility',
    speed: 'Speed',
    throwing: 'Throwing'
};

const COLOR_OPTIONS = [
    '#ef4444', '#f97316', '#fbbf24', '#10b981', '#3b82f6',
    '#8b5cf6', '#06b6d4', '#64748b', '#000000', '#ffffff'
];

export default function RosterModal({ isOpen, onClose }) {
    const { roster, loading, addPlayer, updatePlayer, removePlayer } = useRoster();
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('rank'); // 'rank', 'name', 'number'
    const [selectedPlayerId, setSelectedPlayerId] = useState(null);
    const [isAdding, setIsAdding] = useState(false);

    const filteredRoster = useMemo(() => {
        let list = [...roster];

        if (searchTerm) {
            const lowSearch = searchTerm.toLowerCase();
            list = list.filter(p =>
                p.name.toLowerCase().includes(lowSearch) ||
                p.number.toString().includes(lowSearch) ||
                p.position.toLowerCase().includes(lowSearch)
            );
        }

        list.sort((a, b) => {
            if (sortBy === 'rank') return (b.overallRank || 0) - (a.overallRank || 0);
            if (sortBy === 'name') return a.name.localeCompare(b.name);
            if (sortBy === 'number') return parseInt(a.number || 0) - parseInt(b.number || 0);
            return 0;
        });

        return list;
    }, [roster, searchTerm, sortBy]);

    const selectedPlayer = useMemo(() =>
        roster.find(p => p.id === selectedPlayerId),
        [roster, selectedPlayerId]);

    const handleAdd = async () => {
        const p = await addPlayer({ name: 'New Player' });
        setSelectedPlayerId(p.id);
        setIsAdding(false);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
            <div className="bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
                {/* Header */}
                <div className="p-6 border-b border-gray-800 flex justify-between items-center bg-gray-900">
                    <div>
                        <h2 className="text-2xl font-black text-white flex items-center gap-3">
                            <span className="text-blue-500">📋</span> Team Roster
                        </h2>
                        <p className="text-gray-500 text-xs font-bold uppercase tracking-widest mt-1">Manage players and rank attributes</p>
                    </div>
                    <button onClick={onClose} className="text-gray-500 hover:text-white text-2xl transition-colors">×</button>
                </div>

                <div className="flex flex-1 overflow-hidden">
                    {/* Left Sidebar: Player List */}
                    <div className="w-1/3 border-r border-gray-800 flex flex-col bg-gray-950/50">
                        <div className="p-4 border-b border-gray-800 space-y-3">
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Search players..."
                                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-blue-500 transition-all font-medium"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                <span className="absolute right-3 top-2 text-gray-600">🔍</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <select
                                    className="bg-transparent text-[10px] text-gray-500 font-black uppercase tracking-widest outline-none cursor-pointer hover:text-blue-400 transition-colors"
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                >
                                    <option value="rank">Sort by Rank</option>
                                    <option value="name">Sort by Name</option>
                                    <option value="number">Sort by Number</option>
                                </select>
                                <button
                                    onClick={handleAdd}
                                    className="text-[10px] bg-blue-600 hover:bg-blue-500 text-white px-2 py-1 rounded font-black uppercase tracking-tighter transition-all active:scale-95"
                                >+ Add Player</button>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-2 space-y-1 qr-custom-scrollbar">
                            {loading ? (
                                <div className="text-center py-8 text-gray-600 animate-pulse text-xs font-bold uppercase">Loading Roster...</div>
                            ) : filteredRoster.length === 0 ? (
                                <div className="text-center py-8 text-gray-600 text-xs font-bold uppercase">No players found</div>
                            ) : (
                                filteredRoster.map(p => (
                                    <div
                                        key={p.id}
                                        onClick={() => setSelectedPlayerId(p.id)}
                                        className={`p-3 rounded-xl cursor-pointer transition-all flex items-center gap-3 border ${selectedPlayerId === p.id ? 'bg-blue-600/20 border-blue-500/50 shadow-lg' : 'hover:bg-white/5 border-transparent'}`}
                                    >
                                        <div
                                            className="w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-black shadow-sm"
                                            style={{ backgroundColor: p.iconColor || '#3b82f6', color: ['#ffffff', '#fbbf24'].includes(p.iconColor) ? 'black' : 'white' }}
                                        >
                                            {p.number || '?'}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-bold text-white truncate">{p.name || 'Unnamed'}</div>
                                            <div className="text-[10px] text-gray-500 font-black uppercase tracking-tighter">{p.position || 'N/A'}</div>
                                        </div>
                                        <div className="text-xs font-black text-blue-400 bg-blue-400/10 px-1.5 py-0.5 rounded leading-none">
                                            {p.overallRank || 5}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Right Side: Player Details */}
                    <div className="flex-1 overflow-y-auto p-8 bg-gray-900 qr-custom-scrollbar">
                        {selectedPlayer ? (
                            <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-300">
                                {/* Basic Info Section */}
                                <div className="grid grid-cols-3 gap-6">
                                    <div className="col-span-1 space-y-4">
                                        <div>
                                            <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest block mb-1">Icon Color</label>
                                            <div className="flex flex-wrap gap-1.5">
                                                {COLOR_OPTIONS.map(c => (
                                                    <button
                                                        key={c}
                                                        className={`w-6 h-6 rounded-full border-2 transition-all hover:scale-110 ${selectedPlayer.iconColor === c ? 'border-white ring-2 ring-blue-500 ring-offset-2 ring-offset-gray-900' : 'border-transparent opacity-60 hover:opacity-100'}`}
                                                        style={{ backgroundColor: c }}
                                                        onClick={() => updatePlayer(selectedPlayer.id, { iconColor: c })}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                        <div className="pt-2">
                                            <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest block mb-1">Overall Rank (1-10)</label>
                                            <div className="flex items-center gap-4">
                                                <input
                                                    type="range" min="1" max="10" step="1"
                                                    className="flex-1 h-1.5 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
                                                    value={selectedPlayer.overallRank || 5}
                                                    onChange={(e) => updatePlayer(selectedPlayer.id, { overallRank: parseInt(e.target.value) })}
                                                />
                                                <span className="text-xl font-black text-blue-400 w-8 text-center">{selectedPlayer.overallRank || 5}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-span-2 space-y-4 bg-gray-800/30 p-6 rounded-2xl border border-gray-800 shadow-inner">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest block mb-1">Player Name</label>
                                                <input
                                                    type="text"
                                                    className="w-full bg-gray-950 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500 transition-all font-bold"
                                                    value={selectedPlayer.name}
                                                    onChange={(e) => updatePlayer(selectedPlayer.id, { name: e.target.value })}
                                                    placeholder="e.g. Mike Johnson"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest block mb-1">Uniform #</label>
                                                <input
                                                    type="text"
                                                    className="w-full bg-gray-950 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500 transition-all font-bold"
                                                    value={selectedPlayer.number}
                                                    onChange={(e) => updatePlayer(selectedPlayer.id, { number: e.target.value })}
                                                    placeholder="e.g. 12"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest block mb-1">Primary Position</label>
                                            <input
                                                type="text"
                                                className="w-full bg-gray-950 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500 transition-all font-bold"
                                                value={selectedPlayer.position}
                                                onChange={(e) => updatePlayer(selectedPlayer.id, { position: e.target.value })}
                                                placeholder="e.g. QB / CB"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Attributes Section */}
                                <div className="space-y-4">
                                    <h3 className="text-xs font-black text-gray-400 uppercase tracking-[0.2em] flex items-center gap-2">
                                        <div className="h-[1px] flex-1 bg-gray-800"></div>
                                        Skill Ratings
                                        <div className="h-[1px] flex-1 bg-gray-800"></div>
                                    </h3>
                                    <div className="grid grid-cols-2 gap-x-12 gap-y-6 bg-gray-800/20 p-6 rounded-2xl border border-gray-800">
                                        {Object.keys(ATTR_LABELS).map(key => (
                                            <div key={key} className="space-y-2">
                                                <div className="flex justify-between items-center px-1">
                                                    <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">{ATTR_LABELS[key]}</label>
                                                    <span className="text-xs font-black text-white bg-gray-700 px-2 py-0.5 rounded-full">{selectedPlayer.attributes?.[key] || 5}</span>
                                                </div>
                                                <input
                                                    type="range" min="1" max="10" step="1"
                                                    className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
                                                    value={selectedPlayer.attributes?.[key] || 5}
                                                    onChange={(e) => {
                                                        const newVal = parseInt(e.target.value);
                                                        updatePlayer(selectedPlayer.id, {
                                                            attributes: { ...selectedPlayer.attributes, [key]: newVal }
                                                        });
                                                    }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="pt-8 border-t border-gray-800 flex justify-between items-center">
                                    <button
                                        onClick={() => {
                                            if (window.confirm(`Delete ${selectedPlayer.name || 'this player'} from roster?`)) {
                                                removePlayer(selectedPlayer.id);
                                                setSelectedPlayerId(null);
                                            }
                                        }}
                                        className="text-red-500 hover:text-red-400 text-[10px] font-black uppercase tracking-wider px-4 py-2 hover:bg-red-500/10 rounded-lg transition-colors"
                                    >Delete Player</button>
                                    <button
                                        onClick={() => setSelectedPlayerId(null)}
                                        className="bg-gray-800 hover:bg-gray-700 text-white px-6 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all"
                                    >Deselect</button>
                                </div>
                            </div>
                        ) : (
                            <div className="h-full flex flex-col items-center justify-center text-center space-y-4 opacity-30 select-none">
                                <div className="text-7xl">👤</div>
                                <div>
                                    <h3 className="text-xl font-black text-white">No Player Selected</h3>
                                    <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mt-1">Choose a player from the list to view and edit details</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
